import { TICK_DT } from "@rietz/shared";

/**
 * Latency estimation and the shared "server timeline". Two clocks, kept apart:
 * the WALL OFFSET (`sNow + rtt/2 - now`, reported only, never used for
 * rendering) and the TICK TIMELINE (`tick * TICK_DT * 1000`, the timeline
 * snapshots are stamped on, anchored on `welcome.tick` and re-estimated from
 * snapshot arrivals using only `rtt`). Both are EMA-smoothed; the tick timeline
 * hard-resyncs after a large step.
 */

const TICK_MS = TICK_DT * 1000;

/** Ping cadence, ms. */
const PING_INTERVAL_MS = 1000;

/** EMA weight for round-trip time. */
const RTT_ALPHA = 0.1;
/** EMA weight for the wall-clock offset. */
const OFFSET_ALPHA = 0.1;
/**
 * EMA weight for the tick timeline — slower than the others, since it drives
 * rendering and must not chase jitter.
 */
const TICK_ALPHA = 0.05;

/** Beyond this the tick timeline is snapped rather than eased. */
const TICK_RESYNC_MS = 250;

export class NetClock {
  /** Smoothed round-trip time, ms. `0` until the first pong. */
  rttMs = 0;
  /** Smoothed `sNow + rtt/2 - now`, ms. Diagnostics only — see the note above. */
  serverWallOffsetMs = 0;

  /** Local `performance.now()` of the most recent pong. */
  lastPongAtMs = 0;

  private haveRtt = false;
  private haveWallOffset = false;
  private haveTickBase = false;

  /** `estServerNowMs() === performance.now() + tickOffsetMs`. */
  private tickOffsetMs = 0;

  private nextPingAtMs = 0;

  /** Call every frame; returns the `cNow` to ping with, or `null` if not due. */
  pingIfDue(nowMs: number): number | null {
    if (nowMs < this.nextPingAtMs) return null;
    this.nextPingAtMs = nowMs + PING_INTERVAL_MS;
    return nowMs;
  }

  /** Reset the ping schedule so a fresh connection measures rtt immediately. */
  resetPingSchedule(nowMs: number): void {
    this.nextPingAtMs = nowMs;
  }

  onPong(cNow: number, sNow: number, nowMs: number): void {
    const rtt = nowMs - cNow;
    if (!Number.isFinite(rtt) || rtt < 0) return;

    this.rttMs = this.haveRtt ? ema(this.rttMs, rtt, RTT_ALPHA) : rtt;
    this.haveRtt = true;
    this.lastPongAtMs = nowMs;

    const offset = sNow + rtt * 0.5 - nowMs;
    this.serverWallOffsetMs = this.haveWallOffset
      ? ema(this.serverWallOffsetMs, offset, OFFSET_ALPHA)
      : offset;
    this.haveWallOffset = true;
  }

  /** Anchors the tick timeline from `welcome`. */
  onWelcome(tick: number, nowMs: number): void {
    this.haveTickBase = false;
    this.applyTickSample(tick, nowMs);
  }

  /** Refines the tick timeline from every snapshot that arrives. */
  onSnapshot(tick: number, nowMs: number): void {
    this.applyTickSample(tick, nowMs);
  }

  private applyTickSample(tick: number, nowMs: number): void {
    // Produced when the server stood at `tick`, one one-way trip ago.
    const sample = tick * TICK_MS + this.rttMs * 0.5 - nowMs;
    if (!this.haveTickBase) {
      this.tickOffsetMs = sample;
      this.haveTickBase = true;
      return;
    }
    if (Math.abs(sample - this.tickOffsetMs) > TICK_RESYNC_MS) {
      this.tickOffsetMs = sample;
      return;
    }
    this.tickOffsetMs = ema(this.tickOffsetMs, sample, TICK_ALPHA);
  }

  /** True once the tick timeline has an anchor and may be sampled. */
  get synced(): boolean {
    return this.haveTickBase;
  }

  /** Estimated server time, in ms since server tick 0. */
  estServerNowMs(nowMs: number = performance.now()): number {
    return nowMs + this.tickOffsetMs;
  }

  /** Estimated tick the server is currently simulating. */
  estServerTick(nowMs: number = performance.now()): number {
    return Math.floor(this.estServerNowMs(nowMs) / TICK_MS);
  }

  /** Converts a snapshot tick into a timestamp on the tick timeline. */
  static tickToMs(tick: number): number {
    return tick * TICK_MS;
  }

  /** Forgets every estimate; used when a connection drops. */
  reset(): void {
    this.haveRtt = false;
    this.haveWallOffset = false;
    this.haveTickBase = false;
    this.rttMs = 0;
    this.serverWallOffsetMs = 0;
    this.tickOffsetMs = 0;
    this.lastPongAtMs = 0;
  }
}

function ema(prev: number, sample: number, alpha: number): number {
  return prev + (sample - prev) * alpha;
}
